import { useEffect, useState } from 'react'
import {
  TrendingDown,
  LogIn,
  ArrowRight,
  Compass,
  Target,
  Bell,
  Shield,
  Lock,
  CheckCircle2,
  Flame,
  DollarSign,
  CreditCard,
  Sparkles,
  GitBranch,
  EyeOff,
  Plus,
  Wallet,
  AlertTriangle,
  TimerReset,
  X,
} from 'lucide-react'
import { SignInDialog } from '@/auth/SignInDialog'
import { useAuth } from '@/auth/useAuth'
import { isSupabaseConfigured } from '@/repository/supabase/client'
import { consumeSessionExpired } from '@/auth/sessionFlags'
import { AnimatedNumber } from '@/components/ui/AnimatedNumber'
import { formatCurrency, formatPercent } from '@/lib/formatters'
import { softNavigate } from '@/lib/softRouting'

interface LandingPageProps {
  /** Drops the visitor into the app in local-only mode (no auth). */
  onContinueWithoutAccount: () => void
}

/**
 * Marketing landing for unauthenticated visitors.
 *
 * Five-section composition:
 *   1. TopBar           — brand + section anchors + sign-in cluster
 *   2. Hero             — value prop + dual CTA + floating dashboard mockup
 *                         (real AnimatedNumber + handbuilt card mosaic)
 *   3. PrinciplesStrip  — five trust badges, the social-proof slot for an
 *                         invite-only product that doesn't have testimonials
 *                         yet but does have a strong principles posture
 *   4. BentoFeatures    — asymmetric grid; one large card hosts a real
 *                         CSS bar chart of strategy outcomes, smaller cards
 *                         spotlight individual capabilities with concrete
 *                         visuals (mock badges, mini lists, severity chips)
 *   5. HowItWorks       — three numbered steps with stylised UI snippets
 *   6. ClosingCTA       — restate the offer + dual CTA
 *   7. Footer           — minimal metadata
 *
 * Hero counters animate from zero to target on mount via a setTimeout
 * trick that lets `AnimatedNumber` (which only animates on value change)
 * fire its tween. Result: visitor lands, numbers tick up over half a
 * second, the page feels alive without any external animation library.
 *
 * Recharts is deliberately NOT imported here — the strategy bar chart in
 * the bento is a custom CSS implementation. Keeps the landing's initial
 * JS payload minimal so first-paint stays fast.
 */
export function LandingPage({ onContinueWithoutAccount }: LandingPageProps) {
  const [signInOpen, setSignInOpen] = useState(false)
  // Session-expired banner: AuthProvider drops a sessionStorage flag when
  // it observes an unexpected SIGNED_OUT (refresh failure, token revoked).
  // We consume it on mount — the flag is single-use so a page reload after
  // dismissal doesn't re-show the banner. Re-render is preserved across
  // dismissal via local state.
  const [showSessionExpired, setShowSessionExpired] = useState<boolean>(() =>
    consumeSessionExpired()
  )
  const cloudConfigured = isSupabaseConfigured()
  // `isAuthenticated` repurposes every "Sign in" CTA on this page when
  // the visitor is *already* signed in (which happens when they navigate
  // here via the DebtOS logo from inside the app). For that audience the
  // button becomes "Back to app" and routes to `/`, where RootRouter
  // shows the signed-in shell. No SignInDialog flicker, no redundant
  // OTP request — just a clean trip back to the app they came from.
  const { isAuthenticated } = useAuth()
  const handleSignInClick = (): void => {
    if (isAuthenticated) {
      // v0.8.24: softNavigate keeps the AppShell + AuthProvider mounted
      // instead of forcing a full reload through auth hydration. The
      // signed-in "Back to app" tap now lands instantly.
      softNavigate('/')
    } else {
      setSignInOpen(true)
    }
  }

  return (
    <div className="min-h-dvh bg-slate-950 text-white flex flex-col overflow-x-hidden">
      <TopBar
        cloudConfigured={cloudConfigured}
        onSignIn={handleSignInClick}
        isAuthenticated={isAuthenticated}
      />

      {showSessionExpired && !isAuthenticated && cloudConfigured && (
        <SessionExpiredBanner
          onSignIn={() => setSignInOpen(true)}
          onDismiss={() => setShowSessionExpired(false)}
        />
      )}

      <main className="flex-1 flex flex-col">
        <Hero
          cloudConfigured={cloudConfigured}
          onSignIn={handleSignInClick}
          onContinueWithoutAccount={onContinueWithoutAccount}
          isAuthenticated={isAuthenticated}
        />
        <PrinciplesStrip />
        <BentoFeatures />
        <HowItWorks />
        <ClosingCTA
          cloudConfigured={cloudConfigured}
          onSignIn={handleSignInClick}
          onContinueWithoutAccount={onContinueWithoutAccount}
          isAuthenticated={isAuthenticated}
        />
      </main>

      <Footer />

      <SignInDialog open={signInOpen} onClose={() => setSignInOpen(false)} />
    </div>
  )
}

// ─── Session-expired banner ──────────────────────────────────────────────

/**
 * Bounces in below the TopBar when the user was unexpectedly signed out
 * — refresh-token failure, token revoked, auth endpoint unreachable.
 * AuthProvider sets the sessionStorage trigger; LandingPage consumes it
 * on mount. Dismissable (click X). Single-use: a page reload after
 * dismissal does NOT re-show the banner because the flag was consumed.
 *
 * Hidden when the user is already authenticated (which would only
 * happen if they navigated to /landing manually mid-session) and when
 * Supabase isn't configured (no sign-in path = nothing to invite).
 */
function SessionExpiredBanner({
  onSignIn,
  onDismiss,
}: {
  onSignIn: () => void
  onDismiss: () => void
}) {
  return (
    <div
      role="status"
      className="border-b border-amber-500/40 bg-amber-500/10"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 shrink-0 text-amber-300" aria-hidden="true" />
        <div className="flex-1 min-w-0 text-xs sm:text-sm text-amber-100 leading-snug">
          Your session expired — sign in again to pick up where you left off.
        </div>
        <button
          onClick={onSignIn}
          className="shrink-0 px-3 py-1 bg-amber-500/30 hover:bg-amber-500/40 text-amber-50 rounded-md text-xs font-semibold transition-colors"
        >
          Sign in
        </button>
        <button
          onClick={onDismiss}
          aria-label="Dismiss session expired notice"
          className="shrink-0 p-1 text-amber-200/80 hover:text-amber-50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Top bar ─────────────────────────────────────────────────────────────

function TopBar({
  cloudConfigured,
  onSignIn,
  isAuthenticated,
}: {
  cloudConfigured: boolean
  onSignIn: () => void
  isAuthenticated: boolean
}) {
  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-800/40 bg-slate-950/70 backdrop-blur-md"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
        <a href="#top" className="flex items-center gap-2 text-white font-bold text-lg shrink-0">
          <TrendingDown className="w-5 h-5 text-emerald-400" />
          <span>DebtOS</span>
        </a>

        <nav className="hidden md:flex items-center gap-6 ml-6 text-sm text-slate-400">
          <a href="#features" className="hover:text-white transition-colors">
            Features
          </a>
          <a href="#how-it-works" className="hover:text-white transition-colors">
            How it works
          </a>
          <a href="#principles" className="hover:text-white transition-colors">
            Principles
          </a>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {cloudConfigured ? (
            <button
              onClick={onSignIn}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
            >
              {isAuthenticated ? (
                <>
                  <ArrowRight className="w-4 h-4" />
                  Back to app
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign in
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </header>
  )
}

// ─── Hero ────────────────────────────────────────────────────────────────

function Hero({
  cloudConfigured,
  onSignIn,
  onContinueWithoutAccount,
  isAuthenticated,
}: {
  cloudConfigured: boolean
  onSignIn: () => void
  onContinueWithoutAccount: () => void
  isAuthenticated: boolean
}) {
  return (
    <section id="top" className="relative px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16">
      {/* Decorative gradient field — radial emerald glow at the top-left,
          diagonal blue wash on the right. Pure CSS, no images. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -top-20 right-0 w-[480px] h-[480px] rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
      </div>

      <div className="relative max-w-screen-xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs font-medium text-emerald-300">
            <Compass className="w-3.5 h-3.5" />
            Recovery-first personal-debt operating system
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white leading-[1.05]">
            Map your way back to <span className="text-emerald-400">zero debt</span>.
          </h1>

          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            A spreadsheet can hold your debts. DebtOS works them — four payoff strategies scored on
            your real APRs, alerts before 0% promos expire, and monthly reconciliation that keeps
            the plan honest. The dashboard below is running the real engine on demo data.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            {cloudConfigured ? (
              <button
                onClick={onSignIn}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-600/20"
              >
                {isAuthenticated ? (
                  <>
                    <ArrowRight className="w-4 h-4" />
                    Back to your app
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Sign in with magic link
                  </>
                )}
              </button>
            ) : null}
            {!isAuthenticated && (
              <button
                onClick={onContinueWithoutAccount}
                className={[
                  'inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors',
                  cloudConfigured
                    ? 'border border-slate-700 hover:border-slate-600 text-slate-200 hover:bg-slate-900'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20',
                ].join(' ')}
              >
                Try the demo (local-only)
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <p className="mt-5 text-xs text-slate-500 max-w-md mx-auto">
            {isAuthenticated ? (
              <>You&rsquo;re signed in. Click above to return to your dashboard.</>
            ) : cloudConfigured ? (
              <>
                Sign-in is invite-only and passwordless. Without an account the app still works
                locally on this device — nothing leaves the browser.
              </>
            ) : (
              <>Local-only mode — your data stays on this device, never leaves.</>
            )}
          </p>
        </div>

        <HeroMockup />
      </div>
    </section>
  )
}

/**
 * The Hero's visual centrepiece — a composed mock of the live Dashboard.
 * Renders three stacked surfaces: a "browser frame" outer chrome, a primary
 * RecoveryProgressCard preview, and a row of four SummaryCard previews,
 * plus two floating background cards rotated slightly for depth.
 *
 * Numbers animate on mount via `useDelayedTarget` — starts at zero, settles
 * at the target over `AnimatedNumber`'s default 500ms ease-out window.
 * Cards use the same slate-900/border-slate-800 palette as the real
 * Dashboard so the visual is honest, not "marketing-ese."
 */
function HeroMockup() {
  return (
    <div className="relative mt-14 sm:mt-20 max-w-4xl mx-auto px-2">
      {/* Backdrop glow */}
      <div className="absolute -inset-x-6 -inset-y-6 sm:-inset-x-12 sm:-inset-y-12 bg-gradient-to-br from-emerald-500/15 via-transparent to-blue-500/15 rounded-[3rem] blur-3xl" />

      {/* Floating accessory cards behind the main panel */}
      <div className="hidden md:block absolute -left-6 top-10 -rotate-6 w-40 z-0">
        <FloatingMiniCard
          icon={<Bell className="w-4 h-4" />}
          tone="amber"
          title="0% promo expires in 21 days"
          subtitle="Bigbank Card → 23.99%"
        />
      </div>
      <div className="hidden md:block absolute -right-6 top-32 rotate-6 w-44 z-0">
        <FloatingMiniCard
          icon={<Sparkles className="w-4 h-4" />}
          tone="emerald"
          title="Avalanche saves $8.4k"
          subtitle="47 mo vs 89 mo minimum-only"
        />
      </div>

      {/* Main browser-style frame */}
      <div className="relative z-10 bg-slate-900/90 backdrop-blur-sm border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
        <BrowserChrome />

        <div className="p-4 sm:p-6 space-y-4 bg-slate-950/60">
          <MockRecoveryCard />
          <MockSummaryRow />
          <MockStrategyComparison />
        </div>
      </div>
    </div>
  )
}

function BrowserChrome() {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 border-b border-slate-800">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
      <div className="ml-3 flex-1 max-w-xs">
        <div className="text-[10px] font-mono text-slate-500 bg-slate-950/60 border border-slate-800 rounded px-2 py-0.5">
          debt-os.pages.dev/dashboard
        </div>
      </div>
    </div>
  )
}

interface FloatingMiniCardProps {
  icon: React.ReactNode
  tone: 'amber' | 'emerald' | 'red'
  title: string
  subtitle: string
}

function FloatingMiniCard({ icon, tone, title, subtitle }: FloatingMiniCardProps) {
  const toneStyle = {
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    red: 'bg-red-500/10 border-red-500/30 text-red-300',
  }[tone]
  return (
    <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-800 rounded-xl p-3 shadow-xl">
      <div
        className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${toneStyle}`}
      >
        {icon}
      </div>
      <div className="mt-2 text-[11px] font-semibold text-white leading-tight">{title}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{subtitle}</div>
    </div>
  )
}

/** Inside the mockup: emulates RecoveryProgressCard with animated numbers. */
function MockRecoveryCard() {
  const percent = useDelayedTarget(37.4)
  const paidDown = useDelayedTarget(15710)
  const current = useDelayedTarget(26290)

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            Recovery progress
          </div>
          <div className="mt-1 text-4xl sm:text-5xl font-bold font-mono text-white">
            <AnimatedNumber value={percent} format={(n) => `${n.toFixed(1)}%`} />
          </div>
          <p className="text-sm text-slate-400 mt-1">of the way back</p>
        </div>
      </div>

      <div className="mt-4 h-[3px] bg-slate-800/60 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500/80 transition-[width] duration-1000 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="mt-4 pt-4 border-t border-slate-800/50 grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Started at</div>
          <div className="text-slate-200 font-mono">$42,000</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Jan 1</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Paid down</div>
          <div className="text-emerald-300 font-mono">
            <AnimatedNumber value={paidDown} format={formatCurrency} />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Currently</div>
          <div className="text-slate-200 font-mono">
            <AnimatedNumber value={current} format={formatCurrency} />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Inside the mockup: emulates SummaryCards row. */
function MockSummaryRow() {
  const totalDebt = useDelayedTarget(26290)
  const minPay = useDelayedTarget(850)
  const avgApr = useDelayedTarget(18.4)
  const burn = useDelayedTarget(402)
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MockSummaryCard
        icon={<DollarSign className="w-4 h-4" />}
        bg="bg-red-500/10"
        color="text-red-400"
        label="Total Debt"
        value={<AnimatedNumber value={totalDebt} format={formatCurrency} />}
        sub="6 accounts"
      />
      <MockSummaryCard
        icon={<CreditCard className="w-4 h-4" />}
        bg="bg-orange-500/10"
        color="text-orange-400"
        label="Monthly Minimums"
        value={<AnimatedNumber value={minPay} format={formatCurrency} />}
        sub="required each month"
      />
      <MockSummaryCard
        icon={<TrendingDown className="w-4 h-4" />}
        bg="bg-yellow-500/10"
        color="text-yellow-400"
        label="Weighted Avg APR"
        value={<AnimatedNumber value={avgApr} format={formatPercent} />}
        sub="across all debts"
      />
      <MockSummaryCard
        icon={<Flame className="w-4 h-4" />}
        bg="bg-red-500/10"
        color="text-red-400"
        label="Monthly Interest Burn"
        value={<AnimatedNumber value={burn} format={formatCurrency} />}
        sub="money on fire"
      />
    </div>
  )
}

interface MockSummaryCardProps {
  icon: React.ReactNode
  bg: string
  color: string
  label: string
  value: React.ReactNode
  sub: string
}

function MockSummaryCard({ icon, bg, color, label, value, sub }: MockSummaryCardProps) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className={['p-1 rounded', bg, color].join(' ')}>{icon}</div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider truncate">
          {label}
        </span>
      </div>
      <div className="text-base sm:text-lg font-bold text-white font-mono">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  )
}

/** Inside the mockup: a horizontal bar chart of payoff strategies. */
function MockStrategyComparison() {
  const months = useDelayedTarget(1, 800) // gate that fires after 800ms
  const animate = months > 0.5
  const rows: { name: string; months: number; bg: string; sub: string }[] = [
    { name: 'Avalanche', months: 47, bg: 'bg-emerald-500/80', sub: 'highest APR first' },
    { name: 'Snowball', months: 51, bg: 'bg-blue-500/70', sub: 'smallest balance first' },
    { name: 'Hybrid', months: 48, bg: 'bg-purple-500/70', sub: 'top two by APR + balance' },
    { name: 'Minimum only', months: 89, bg: 'bg-slate-700', sub: 'lender minimums only' },
  ]
  const maxMonths = 89

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
          Strategy comparison
        </div>
        <div className="text-[10px] text-slate-500">months to payoff · lower is better</div>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div
            key={r.name}
            className="grid grid-cols-[100px_1fr_auto] sm:grid-cols-[140px_1fr_auto] items-center gap-3"
          >
            <div className="text-xs sm:text-sm text-slate-200 font-medium truncate">{r.name}</div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${r.bg} transition-[width] duration-1000 ease-out`}
                style={{ width: animate ? `${(r.months / maxMonths) * 100}%` : '0%' }}
              />
            </div>
            <div className="text-xs sm:text-sm text-slate-400 font-mono w-10 sm:w-12 text-right">
              {r.months} mo
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Drives an "animate from 0 → target on mount" effect by holding the
 * displayed value at 0 for a beat, then flipping to the real target so
 * AnimatedNumber's value-changed tween fires. Accepts an optional
 * pre-target wait to stagger animations across cards.
 */
function useDelayedTarget(target: number, delayMs = 80): number {
  const [value, setValue] = useState(0)
  useEffect(() => {
    const t = setTimeout(() => setValue(target), delayMs)
    return () => clearTimeout(t)
  }, [target, delayMs])
  return value
}

// ─── Principles strip ───────────────────────────────────────────────────

function PrinciplesStrip() {
  const principles: { icon: React.ReactNode; title: string; body: string }[] = [
    {
      icon: <Lock className="w-4 h-4" />,
      title: 'Works offline, every time',
      body: 'No internet? No problem. Your dashboard, your data, your plan — all on this device.',
    },
    {
      icon: <Shield className="w-4 h-4" />,
      title: 'Your numbers stay yours',
      body: 'We never see your balances. Cloud sync is opt-in and locked to your account — and to the household members you invite.',
    },
    {
      icon: <EyeOff className="w-4 h-4" />,
      title: "We don't track you, period",
      body: 'No analytics, no marketing pixels, no third-party scripts.',
    },
    {
      icon: <DollarSign className="w-4 h-4" />,
      title: 'Free at this scale',
      body: 'No paywalls. Pay nothing to recover.',
    },
    {
      icon: <CheckCircle2 className="w-4 h-4" />,
      title: 'Brutally honest math',
      body: 'If a strategy never pays off, DebtOS says so. No upbeat rounding when the numbers are bleak.',
    },
  ]
  return (
    <section id="principles" className="px-4 sm:px-6 py-12 sm:py-16 border-t border-slate-900">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            How we built it
          </div>
          <h2 className="mt-2 text-xl sm:text-2xl font-semibold text-white">
            Principles, not promises
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
          {principles.map((p) => (
            <div
              key={p.title}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                {p.icon}
              </div>
              <div className="mt-3 text-sm font-semibold text-white">{p.title}</div>
              <div className="mt-1 text-xs text-slate-400 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Bento features ──────────────────────────────────────────────────────

/**
 * Asymmetric bento grid: one tall left card hosts the strategy comparison
 * chart (the app's marquee differentiator), surrounded by smaller cards
 * each spotlighting a single capability with a concrete visual artifact
 * (severity chip row, promo banner, BT offer pill, etc.). Each smaller
 * card pulls visual language directly from the actual product.
 */
function BentoFeatures() {
  return (
    <section id="features" className="px-4 sm:px-6 py-16 sm:py-24 border-t border-slate-900">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            Features
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-white">
            Everything you need to climb out
          </h2>
          <p className="mt-3 text-slate-400 text-sm sm:text-base">
            Four modules that turn "I have a lot of debt" into "here's the plan, here's the
            progress, here's the next decision."
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {/* Large card: Strategy engine, spans 2 cols + 2 rows on md+ */}
          <div className="md:col-span-2 md:row-span-2">
            <BentoCard tone="emerald" badge="Strategy Engine">
              <BentoTitle>The cheapest path to zero, simulated 360 months out</BentoTitle>
              <BentoBody>
                Avalanche, Snowball, Hybrid, and Minimum-Only — each scored against your real
                balances, APRs, and promo windows. Pick the strategy whose tradeoffs match your
                psychology, not someone else's gospel.
              </BentoBody>
              <div className="mt-6">
                <MiniStrategyBars />
              </div>
            </BentoCard>
          </div>

          {/* Right column top: Recovery progress headline */}
          <div className="md:col-span-1">
            <BentoCard tone="emerald" badge="Recovery anchor">
              <BentoTitle>Anchored to your real day-zero</BentoTitle>
              <BentoBody>
                Set your peak debt and start date — no auto-anchor at install. The percent you quote
                is honest.
              </BentoBody>
              <div className="mt-4">
                <MiniRecoveryStat />
              </div>
            </BentoCard>
          </div>

          {/* Right column bottom: Promo & BT watch */}
          <div className="md:col-span-1">
            <BentoCard tone="amber" badge="Promo &amp; BT Watch">
              <BentoTitle>0% expiries flagged before they bite</BentoTitle>
              <BentoBody>
                Balance-transfer offers scored as accept / skip / consider with the math attached.
              </BentoBody>
              <div className="mt-4 space-y-2">
                <MiniPromoBanner
                  tone="amber"
                  label="Expires in 21 days"
                  detail="Bigbank Card · 0% → 23.99%"
                />
                <MiniPromoBanner
                  tone="emerald"
                  label="BT offer: Accept"
                  detail="Bank A · saves est. $612"
                />
              </div>
            </BentoCard>
          </div>

          {/* Bottom row card 1: Cash Flow */}
          <div>
            <BentoCard tone="blue" badge="Cash Flow">
              <BentoTitle>Plan and reconcile every month</BentoTitle>
              <BentoBody>
                Income against the survival floor, plan vs. actual, closed months frozen as honest
                history. Invite your partner to plan it together — debts tagged joint or yours.
              </BentoBody>
              <div className="mt-4">
                <MiniSurvivalFloor />
              </div>
            </BentoCard>
          </div>

          {/* Bottom row card 2: APR Severity */}
          <div>
            <BentoCard tone="red" badge="APR severity">
              <BentoTitle>The numbers in the registry tell you what to attack</BentoTitle>
              <BentoBody>
                Color-coded severity makes a 30-debt portfolio scannable in seconds.
              </BentoBody>
              <div className="mt-4 space-y-1.5">
                <MiniSeverityRow color="red" label="Critical" range=">=20% APR" />
                <MiniSeverityRow color="orange" label="High" range="10-20%" />
                <MiniSeverityRow color="yellow" label="Medium" range="5-10%" />
                <MiniSeverityRow color="emerald" label="Low" range="<5%" />
              </div>
            </BentoCard>
          </div>

          {/* Bottom row card 3: Path to Freedom (v0.8.3) — replaces the
              former "Calc invariants" card. The principles strip already
              covers our math-testability flex; this card showcases the
              user-visible feature instead. */}
          <div>
            <BentoCard tone="purple" badge="Path to Freedom">
              <BentoTitle>Every debt becomes a named milestone</BentoTitle>
              <BentoBody>
                The order your debts clear, each with a projected date. Debt-free in N months,
                visible at a glance.
              </BentoBody>
              <div className="mt-4">
                <MiniMilestoneList />
              </div>
            </BentoCard>
          </div>
        </div>
      </div>
    </section>
  )
}

interface BentoCardProps {
  tone: 'emerald' | 'blue' | 'amber' | 'red' | 'purple'
  badge: React.ReactNode
  children: React.ReactNode
}

const TONE_RING: Record<BentoCardProps['tone'], string> = {
  emerald: 'from-emerald-500/15 to-transparent',
  blue: 'from-blue-500/15 to-transparent',
  amber: 'from-amber-500/15 to-transparent',
  red: 'from-red-500/15 to-transparent',
  purple: 'from-purple-500/15 to-transparent',
}

const TONE_BADGE: Record<BentoCardProps['tone'], string> = {
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  red: 'bg-red-500/10 border-red-500/30 text-red-300',
  purple: 'bg-purple-500/10 border-purple-500/30 text-purple-300',
}

function BentoCard({ tone, badge, children }: BentoCardProps) {
  return (
    <div
      className={`relative h-full bg-slate-900/40 border border-slate-800 hover:border-slate-700 transition-colors rounded-2xl p-5 sm:p-6 overflow-hidden`}
    >
      <div
        className={`pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br ${TONE_RING[tone]} blur-2xl`}
        aria-hidden="true"
      />
      <div className="relative">
        <div
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-semibold ${TONE_BADGE[tone]}`}
        >
          {badge}
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}

function BentoTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-lg sm:text-xl font-semibold text-white leading-snug">{children}</h3>
}

function BentoBody({ children }: { children: React.ReactNode }) {
  return <p className="mt-2 text-sm text-slate-400 leading-relaxed">{children}</p>
}

function MiniStrategyBars() {
  const rows: { name: string; months: number; bg: string }[] = [
    { name: 'Avalanche', months: 47, bg: 'bg-emerald-500/80' },
    { name: 'Snowball', months: 51, bg: 'bg-blue-500/70' },
    { name: 'Hybrid', months: 48, bg: 'bg-purple-500/70' },
    { name: 'Minimum only', months: 89, bg: 'bg-slate-700' },
  ]
  const maxMonths = 89
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-4 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
          Strategy outcomes
        </div>
        <div className="text-[10px] text-slate-500">months to payoff</div>
      </div>
      {rows.map((r) => (
        <div key={r.name} className="grid grid-cols-[110px_1fr_auto] items-center gap-3">
          <div className="text-xs text-slate-200">{r.name}</div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${r.bg}`}
              style={{ width: `${(r.months / maxMonths) * 100}%` }}
            />
          </div>
          <div className="text-xs text-slate-400 font-mono w-10 text-right">{r.months}mo</div>
        </div>
      ))}
      <div className="pt-2 mt-2 border-t border-slate-800/60 flex items-center justify-between text-[11px]">
        <span className="text-slate-500">Saved vs minimums</span>
        <span className="text-emerald-300 font-semibold font-mono">$8,420</span>
      </div>
    </div>
  )
}

function MiniRecoveryStat() {
  const percent = useDelayedTarget(37.4)
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-4">
      <div className="text-3xl font-bold text-white font-mono">
        <AnimatedNumber value={percent} format={(n) => `${n.toFixed(1)}%`} />
      </div>
      <div className="text-xs text-slate-400 mt-0.5">of the way back since Jan 1</div>
      <div className="mt-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500/80 transition-[width] duration-1000 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

interface MiniPromoBannerProps {
  tone: 'amber' | 'emerald'
  label: string
  detail: string
}

function MiniPromoBanner({ tone, label, detail }: MiniPromoBannerProps) {
  const toneClass = {
    amber: 'bg-amber-500/10 border-amber-500/30',
    emerald: 'bg-emerald-500/10 border-emerald-500/30',
  }[tone]
  const iconClass = {
    amber: 'text-amber-400',
    emerald: 'text-emerald-400',
  }[tone]
  return (
    <div className={`flex items-center gap-2.5 ${toneClass} border rounded-lg px-2.5 py-1.5`}>
      <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${iconClass}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold text-white truncate">{label}</div>
        <div className="text-[10px] text-slate-400 truncate">{detail}</div>
      </div>
    </div>
  )
}

interface MiniSeverityRowProps {
  color: 'red' | 'orange' | 'yellow' | 'emerald'
  label: string
  range: string
}

function MiniSeverityRow({ color, label, range }: MiniSeverityRowProps) {
  const dotClass = {
    red: 'bg-red-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    emerald: 'bg-emerald-500',
  }[color]
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-2 h-2 rounded-full ${dotClass}`} />
      <span className="text-slate-200 font-medium">{label}</span>
      <span className="text-slate-500 font-mono text-[10px] ml-auto">{range}</span>
    </div>
  )
}

function MiniSurvivalFloor() {
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-4">
      <div className="flex items-end gap-2 mb-3">
        <Wallet className="w-4 h-4 text-blue-400" />
        <div className="text-[10px] uppercase tracking-wider text-slate-400">May 2026</div>
      </div>
      <div className="space-y-2 text-xs">
        <Row label="Income" value="+$6,400" valueClass="text-emerald-300" />
        <Row label="Essentials" value="−$2,150" valueClass="text-slate-300" />
        <Row label="Survival floor" value="$3,000" valueClass="text-amber-300" />
        <div className="border-t border-slate-800/60 pt-2 flex items-center justify-between">
          <span className="text-slate-400 text-xs">Surplus to debt</span>
          <span className="text-emerald-300 font-mono text-xs font-semibold">$1,250</span>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value, valueClass }: { label: string; value: string; valueClass: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className={`font-mono ${valueClass}`}>{value}</span>
    </div>
  )
}

/**
 * Mock milestone list inside the "Path to Freedom" bento card. Mirrors
 * the visual language of the real `MilestoneCard` (debt name + APR
 * badge + "in N months" right-aligned) at a smaller scale so the card
 * shows what the feature *looks like*, not just what it describes.
 */
function MiniMilestoneList() {
  const rows: { num: number; name: string; apr: string; aprTone: string; months: string }[] = [
    {
      num: 1,
      name: 'Acme Visa',
      apr: '29.9%',
      aprTone: 'bg-red-500/10 text-red-300 border-red-500/30',
      months: 'in 4 mo',
    },
    {
      num: 2,
      name: 'Bigbank Card',
      apr: '19.7%',
      aprTone: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
      months: 'in 9 mo',
    },
    {
      num: 3,
      name: 'Personal Loan',
      apr: '8.0%',
      aprTone: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30',
      months: 'in 27 mo',
    },
  ]
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-3 space-y-2">
      {rows.map((r, i) => (
        <div key={r.num} className="flex items-center gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono font-semibold flex items-center justify-center">
            {r.num}
          </span>
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            <span className="text-xs text-white font-medium truncate">{r.name}</span>
            <span
              className={`shrink-0 px-1 py-0 rounded border text-[9px] font-mono font-medium ${r.aprTone}`}
            >
              {r.apr}
            </span>
          </div>
          <span className="text-[10px] text-emerald-300 font-mono font-semibold">{r.months}</span>
          {i < rows.length - 1 ? null : null}
        </div>
      ))}
      <div className="pt-1.5 mt-1 border-t border-slate-800/60 flex items-center justify-between text-[10px]">
        <span className="text-slate-500">Debt-free in</span>
        <span className="text-emerald-300 font-semibold font-mono">27 mo</span>
      </div>
    </div>
  )
}

// ─── How it works ────────────────────────────────────────────────────────

function HowItWorks() {
  const steps: {
    num: string
    icon: React.ReactNode
    title: string
    body: string
    mock: React.ReactNode
  }[] = [
    {
      num: '01',
      icon: <Plus className="w-4 h-4" />,
      title: 'Add your accounts',
      body: 'Lender, balance, APR, minimum, due day. Promo windows and balance-transfer offers if you have them.',
      mock: <Step1Mock />,
    },
    {
      num: '02',
      icon: <Target className="w-4 h-4" />,
      title: 'Pick a strategy',
      body: 'Avalanche minimises interest. Snowball maximises momentum. Hybrid splits the difference. The engine ranks them by months and dollars.',
      mock: <Step2Mock />,
    },
    {
      num: '03',
      icon: <TimerReset className="w-4 h-4" />,
      title: 'Plan and close each month',
      body: 'Set planned payments. Reconcile actuals at month close. The history is frozen; the plan stays live.',
      mock: <Step3Mock />,
    },
  ]
  return (
    <section
      id="how-it-works"
      className="px-4 sm:px-6 py-16 sm:py-24 border-t border-slate-900 bg-slate-950"
    >
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            How it works
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-white">Three steps to the map</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((s) => (
            <div
              key={s.num}
              className="relative bg-slate-900/50 border border-slate-800 rounded-2xl p-5 sm:p-6 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="text-[11px] font-mono text-emerald-400 font-semibold tracking-widest">
                  {s.num}
                </div>
                <div className="w-7 h-7 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  {s.icon}
                </div>
              </div>
              <h3 className="mt-4 text-base font-semibold text-white leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
              <div className="mt-5">{s.mock}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Step1Mock() {
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-3 space-y-2">
      <DebtLine name="Bank A Visa" apr="24.99%" bal="$5,000" color="red" />
      <DebtLine name="Bigbank Card" apr="0% promo" bal="$3,000" color="emerald" />
      <DebtLine name="Test Loan" apr="8.00%" bal="$10,000" color="yellow" />
    </div>
  )
}

interface DebtLineProps {
  name: string
  apr: string
  bal: string
  color: 'red' | 'yellow' | 'emerald'
}

function DebtLine({ name, apr, bal, color }: DebtLineProps) {
  const dotClass = { red: 'bg-red-500', yellow: 'bg-yellow-500', emerald: 'bg-emerald-500' }[color]
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass} shrink-0`} />
        <span className="text-slate-200 truncate">{name}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-slate-500 font-mono text-[10px]">{apr}</span>
        <span className="text-slate-300 font-mono">{bal}</span>
      </div>
    </div>
  )
}

function Step2Mock() {
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-3 space-y-1.5">
      {[
        { name: 'Avalanche', months: '47mo', selected: true },
        { name: 'Snowball', months: '51mo' },
        { name: 'Hybrid', months: '48mo' },
      ].map((s) => (
        <div
          key={s.name}
          className={[
            'flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs border',
            s.selected
              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
              : 'border-transparent text-slate-300',
          ].join(' ')}
        >
          <span className="font-medium">{s.name}</span>
          <span className="font-mono text-[10px]">{s.months}</span>
        </div>
      ))}
    </div>
  )
}

function Step3Mock() {
  return (
    <div className="bg-slate-950/50 border border-slate-800/60 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        <GitBranch className="w-3 h-3" />
        Month timeline
      </div>
      {[
        { month: 'Feb', status: 'Closed', tone: 'emerald' as const },
        { month: 'Mar', status: 'Closed', tone: 'emerald' as const },
        { month: 'Apr', status: 'Closed', tone: 'emerald' as const },
        { month: 'May', status: 'Planning', tone: 'blue' as const },
      ].map((m) => (
        <div key={m.month} className="flex items-center justify-between text-xs">
          <span className="text-slate-200 font-mono">{m.month}</span>
          <span
            className={[
              'px-2 py-0.5 rounded-full text-[10px] font-semibold',
              m.tone === 'emerald'
                ? 'bg-emerald-500/10 text-emerald-300'
                : 'bg-blue-500/10 text-blue-300',
            ].join(' ')}
          >
            {m.status}
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── Closing CTA ─────────────────────────────────────────────────────────

function ClosingCTA({
  cloudConfigured,
  onSignIn,
  onContinueWithoutAccount,
  isAuthenticated,
}: {
  cloudConfigured: boolean
  onSignIn: () => void
  onContinueWithoutAccount: () => void
  isAuthenticated: boolean
}) {
  return (
    <section className="relative px-4 sm:px-6 py-16 sm:py-24 border-t border-slate-900">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-32 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>
      <div className="relative max-w-screen-md mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white">
          {isAuthenticated ? 'Ready to keep going?' : 'Start in 30 seconds.'}
        </h2>
        <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed">
          {isAuthenticated ? (
            <>
              Your session is still active. Jump back into your dashboard whenever you&rsquo;re
              ready.
            </>
          ) : cloudConfigured ? (
            <>
              Sign in with a magic link to sync across devices, or continue without an account and
              keep everything local to this browser.
            </>
          ) : (
            <>
              Click below to load the app in local-only mode. Add your debts, run the strategy
              engine, and the data stays on your device — never sent anywhere.
            </>
          )}
        </p>

        <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
          {cloudConfigured ? (
            <button
              onClick={onSignIn}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-600/20"
            >
              {isAuthenticated ? (
                <>
                  <ArrowRight className="w-4 h-4" />
                  Back to your app
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign in
                </>
              )}
            </button>
          ) : null}
          {!isAuthenticated && (
            <button
              onClick={onContinueWithoutAccount}
              className={[
                'inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors',
                cloudConfigured
                  ? 'border border-slate-700 hover:border-slate-600 text-slate-200 hover:bg-slate-900'
                  : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20',
              ].join(' ')}
            >
              Try the demo (local-only)
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

// ─── Footer ──────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer
      className="border-t border-slate-900 py-6 px-4 sm:px-6"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
    >
      <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-emerald-400" />
          <span>DebtOS · Local-first by default</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="#principles" className="hover:text-slate-300 transition-colors">
            Principles
          </a>
          <a href="#features" className="hover:text-slate-300 transition-colors">
            Features
          </a>
          <span>Invite-only beta</span>
        </div>
      </div>
    </footer>
  )
}
